import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import LiveClassroomViewer from './LiveClassroomViewer';
import TeacherLiveGrid from './TeacherLiveGrid';
import { getLiveSocket } from '../../utils/liveSocket';

const POLL_MS = 60000; // fallback only; Socket.IO delivers changes instantly
const TOAST_MS = 15000;

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const authHeaders = () => {
  const u = getStoredUser();
  return { 'x-user-id': String(u?._id || u?.id || '') };
};

const viewerRole = (role) => {
  if (role === 'student') return 'student';
  if (role === 'parent') return 'parent';
  if (role === 'teacher' || role === 'teacher_admin_level1') return 'teacher';
  return null;
};

/**
 * VIEW-ONLY real-time hub for Students, Parents and Teachers.
 * - Listens for Socket.IO `liveStreamStarted` / `liveStreamStopped`.
 * - Students/Parents: toast only when the event's classId is their class.
 * - Teachers: toast for every class; overlay shows <TeacherLiveGrid/>.
 * - Sticky red banner + a "Live Classes" button with a pulsing red dot while live.
 * The server enforces access independently (see routes/liveStreamRoutes.js).
 */
export default function JoinLiveClassButton({ classId }) {
  const role = viewerRole(getStoredUser()?.role);
  const isTeacher = role === 'teacher';

  const [open, setOpen] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [toast, setToast] = useState(null);
  const myClassId = useRef(classId ? String(classId) : null);
  const toastTimer = useRef(null);

  const showToast = useCallback((text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const onGridCount = useCallback((n) => setIsLive(n > 0), []);

  const checkStatus = useCallback(async (signal) => {
    if (!role) return;
    try {
      if (isTeacher) {
        const res = await axios.get(`${API_BASE_URL}/api/livestream/active-streams`, {
          headers: authHeaders(), signal, timeout: 15000
        });
        setIsLive(Array.isArray(res.data) && res.data.length > 0);
      } else {
        const res = await axios.get(`${API_BASE_URL}/api/livestream/my-stream`, {
          headers: authHeaders(),
          params: classId ? { classId } : undefined,
          signal,
          timeout: 15000
        });
        if (res.data?.classId) myClassId.current = String(res.data.classId);
        setIsLive(!!res.data?.isLive);
      }
    } catch {
      /* keep last known status on transient errors */
    }
  }, [role, isTeacher, classId]);

  useEffect(() => {
    if (!role) return undefined;
    const controller = new AbortController();
    checkStatus(controller.signal);
    const timer = setInterval(() => checkStatus(controller.signal), POLL_MS);

    const socket = getLiveSocket();
    const onStarted = (payload) => {
      if (isTeacher) {
        setIsLive(true);
        showToast(`🔴 Class ${payload?.className || ''} Live Streaming has just started.`);
      } else if (myClassId.current && String(payload?.classId) === myClassId.current) {
        setIsLive(true);
        showToast('🔴 Your Live Class is Running! Click here to join.');
      }
    };
    const onStopped = () => checkStatus();
    socket.on('liveStreamStarted', onStarted);
    socket.on('liveStreamStopped', onStopped);

    return () => {
      controller.abort();
      clearInterval(timer);
      clearTimeout(toastTimer.current);
      socket.off('liveStreamStarted', onStarted);
      socket.off('liveStreamStopped', onStopped);
    };
  }, [role, isTeacher, checkStatus, showToast]);

  if (!role) return null;

  const openViewer = () => { setToast(null); setOpen(true); };

  return (
    <>
      {/* Student/parent banner now lives in <GlobalLivePlayer/> (App.js) */}

      {toast && (
        <button
          type="button"
          onClick={openViewer}
          role="alert"
          className="fixed inset-x-3 top-3 z-[1100] rounded-xl bg-rose-600 px-4 py-4 text-center text-lg font-bold text-white shadow-md active:scale-95"
        >
          {toast}
        </button>
      )}

      <button
        type="button"
        onClick={openViewer}
        className={`fixed bottom-20 right-4 z-[900] flex items-center gap-2 rounded-full px-5 py-3 text-lg font-bold shadow-md transition active:scale-95 ${
          isLive ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-800'
        }`}
      >
        {isLive && (
          <span className="h-3.5 w-3.5 animate-pulse rounded-full bg-white ring-2 ring-red-300" aria-label="Live now" />
        )}
        {isLive ? 'Live Classes' : '📺 Live Classes'}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Live Classroom"
          className="fixed inset-0 z-[1000] overflow-y-auto bg-slate-900/90 p-3"
          onClick={() => setOpen(false)}
        >
          <div className="mx-auto w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mb-3 w-full rounded-xl bg-white py-3 text-lg font-bold text-slate-900 shadow-md active:scale-95"
            >
              ← Go Back
            </button>
            {isTeacher ? (
              <TeacherLiveGrid onCountChange={onGridCount} />
            ) : (
              <LiveClassroomViewer classId={classId} onStatusChange={setIsLive} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
